/**
 * PHASE 4 — minimal quaternion library.
 *
 * Hamilton product, active rotations: rotateVector(v, q) applies q to v
 * (q * v * conj(q)). Conventions:
 *   - components stored {x, y, z, w}, w last
 *   - right-handed axes, positive rotation clockwise when viewed along the
 *     positive axis direction (matches the DeviceOrientation spec)
 *
 * Implemented from scratch (no deps): ~60 lines of math we can unit-test.
 */

export interface Quat {
  x: number;
  y: number;
  z: number;
  w: number;
}

export const IDENTITY: Quat = { x: 0, y: 0, z: 0, w: 1 };

export function quat(x: number, y: number, z: number, w: number): Quat {
  return { x, y, z, w };
}

export function isUnit(q: Quat, tolerance = 1e-6): boolean {
  return Math.abs(norm(q) - 1) < tolerance;
}

export function norm(q: Quat): number {
  return Math.sqrt(q.x * q.x + q.y * q.y + q.z * q.z + q.w * q.w);
}

export function normalize(q: Quat): Quat {
  const n = norm(q);
  if (n === 0) return IDENTITY;
  return { x: q.x / n, y: q.y / n, z: q.z / n, w: q.w / n };
}

/** Hamilton product p * q (apply q first, then p). */
export function mul(p: Quat, q: Quat): Quat {
  return {
    x: p.w * q.x + p.x * q.w + p.y * q.z - p.z * q.y,
    y: p.w * q.y - p.x * q.z + p.y * q.w + p.z * q.x,
    z: p.w * q.z + p.x * q.y - p.y * q.x + p.z * q.w,
    w: p.w * q.w - p.x * q.x - p.y * q.y - p.z * q.z,
  };
}

export function conj(q: Quat): Quat {
  return { x: -q.x, y: -q.y, z: -q.z, w: q.w };
}

/** Active rotation by q: v -> q * v * conj(q). */
export function rotateVector(v: readonly [number, number, number], q: Quat): [number, number, number] {
  const t = mul(q, { x: v[0], y: v[1], z: v[2], w: 0 });
  const r = mul(t, conj(q));
  return [r.x, r.y, r.z];
}

/** Rotation about a NORMALIZED axis by `angleRad`. */
export function fromAxisAngle(axis: readonly [number, number, number], angleRad: number): Quat {
  const half = angleRad / 2;
  const s = Math.sin(half);
  return { x: axis[0] * s, y: axis[1] * s, z: axis[2] * s, w: Math.cos(half) };
}

/** Quaternion for a rotation about the +z axis (device screen normal). */
export function fromZRotation(angleRad: number): Quat {
  return fromAxisAngle([0, 0, 1], angleRad);
}

/**
 * Spherical linear interpolation. `t` in [0, 1]. Handles q and -q ambiguity
 * (shortest path).
 */
export function slerp(a: Quat, b: Quat, t: number): Quat {
  let dot = a.x * b.x + a.y * b.y + a.z * b.z + a.w * b.w;
  let b2 = b;
  if (dot < 0) {
    dot = -dot;
    b2 = { x: -b.x, y: -b.y, z: -b.z, w: -b.w };
  }
  if (dot > 0.9995) {
    // Nearly parallel: linear blend + normalize avoids slerp instability.
    const out = {
      x: a.x + t * (b2.x - a.x),
      y: a.y + t * (b2.y - a.y),
      z: a.z + t * (b2.z - a.z),
      w: a.w + t * (b2.w - a.w),
    };
    return normalize(out);
  }
  const theta = Math.acos(dot);
  const sinTheta = Math.sin(theta);
  const wA = Math.sin((1 - t) * theta) / sinTheta;
  const wB = Math.sin(t * theta) / sinTheta;
  return {
    x: a.x * wA + b2.x * wB,
    y: a.y * wA + b2.y * wB,
    z: a.z * wA + b2.z * wB,
    w: a.w * wA + b2.w * wB,
  };
}

/**
 * Exponential smoothing of two orientations: blend `previous` toward `target`
 * by factor `alpha` in [0, 1] (1 = snap instantly). Uses slerp so the result
 * stays on the manifold.
 */
export function smooth(previous: Quat, target: Quat, alpha: number): Quat {
  if (alpha >= 1) return normalize(target);
  if (alpha <= 0) return normalize(previous);
  return slerp(previous, target, alpha);
}

export function equals(a: Quat, b: Quat, tolerance = 1e-9): boolean {
  return (
    Math.abs(a.x - b.x) < tolerance &&
    Math.abs(a.y - b.y) < tolerance &&
    Math.abs(a.z - b.z) < tolerance &&
    Math.abs(a.w - b.w) < tolerance
  );
}